import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { GroupSummary, GroupSummaryStore, GroupItemStore, GroupType } from 'src/stores/groups-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Card, Row, Tag } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import NewGroupDialog from './group-new';
import { ENV } from 'src/';
import { emptyTableModel } from 'src/common/utils';
import { useNavigate, Link } from 'react-router-dom';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { PermissionsConstants } from 'src/common/types';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { useIdentity } from 'src/core/services/authentication';

const permissions = [PermissionsConstants.ManageGlobalLicenses];

const LicenseManagerGroupListPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config: AppConfiguration = useContext(AppConfig);
  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'name', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10,
    filter: { groupType: GroupType.License },
  } as Query);

  const groupsStore = container.get(GroupSummaryStore);
  const groupFormStore = container.get(GroupItemStore);
  const groupsState = groupsStore.state;
  const groupFormState = groupFormStore.state;
  const identity = useIdentity();
  const [userPermissionsSet] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const hasGlobalLicensemanagerPermission = userPermissionsSet.has(PermissionsConstants.ManageGlobalLicenses);

  const [newGroupVisible, setNewGroupVisible] = useState(false);

  setHeader(t('Groups'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'groups', breadcrumbName: t('Groups') },
  ]);

  useEffect(
    () => {
      groupsStore.loadWithTypes(query, [GroupType.License]);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'name',
        title: t('Name'),
        renderer: (data) => (
          <span>
            <Link to={`${data.id}`}>
              <span>{data.name}</span>
            </Link>
          </span>
        ),
      },
      {
        field: 'organizationId',
        title: t('Organization'),
        renderer: (data: GroupSummary) => (
          <span>
            <Tag>{data.organization}</Tag>
          </span>
        ),
      },
    ],
    data: { ...groupsState.value, isBusy: groupsState.value.isBusy || groupFormState.value.isBusy },
  } as TableModel<GroupSummary>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew
          onNewItem={hasGlobalLicensemanagerPermission ? () => setNewGroupVisible(true) : undefined}
          newItemName={t('AddGroup')}
          canDelete
          onDeleteRow={(item: any) => groupsStore.delete(item.id)}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            groupsStore.loadWithTypes(query, [GroupType.License]);
          }}
          onRefresh={() => groupsStore.loadWithTypes(query, [GroupType.License])}
          model={groupsState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            groupsState.errorMessage.value && (config.environment === ENV.DEV ? formatMessage(groupsState.errorMessage.value) : t('GroupsTableError'))
          }
        />
        {newGroupVisible && (
          <NewGroupDialog
            groupType={GroupType.License}
            onClose={(response) => {
              setNewGroupVisible(false);
              if (response) {
                navigate(`${response.identity}`);
              }
            }}
          />
        )}
      </Card>
    </Row>
  );
};

export default LicenseManagerGroupListPage;
